import { MeetingActivityState, ActivityType } from './../classes/activity/activity.class';
import { DurationType, TimeOffActivity } from '@omni/classes/activity/timeoff.class';
import { PhoneActivity } from '@omni/classes/activity/phone.activity.class';
import { AppointmentActivity } from '@omni/classes/activity/appointment.activity.class';
import { Activity, PhoneCallActivityState } from "../classes/activity/activity.class";
import { EventActivity } from '../classes/events-tool/event.class';
import { intersectionBy, isEmpty } from 'lodash-es';
// Performance optimization: Cache for activity validation
const activityValidationCache = new Map<string, boolean>();
const activityValidationCacheSize = 1000;

// Performance optimization: Cache for date range conflicts
const dateConflictCache = new Map<string, boolean>();
const dateConflictCacheSize = 500;

// Performance optimization: Cache for account visit checks
const accountVisitCache = new Map<string, { isAccountVisit: boolean, isNestedMeeting: boolean }>();

/**
 * Clear caches when they get too large
 */
function clearCacheIfNeeded(cache: Map<string, any>, maxSize: number): void {
  if (cache.size > maxSize) {
    cache.clear();
  }
}

/**
 * Generate cache key for activity validation
 */
function getActivityValidationKey(activity: Activity, userId: string): string {
  return `${activity.ID}_${activity.type}_${activity.state}_${userId}`;
}

/**
 * Generate cache key for date conflict checking
 */
function getDateConflictKey(aStart: Date, aEnd: Date, bStart: Date, bEnd: Date): string {
  return `${aStart?.getTime()}_${aEnd?.getTime()}_${bStart?.getTime()}_${bEnd?.getTime()}`;
}

export function startRecursiveActivityScheduleConflictFlagging(sortedList: Activity[], startingIdx: number, userId: string, shouldCheckAccountVisit: boolean) {
  if (Array.isArray(sortedList) && sortedList.length > 0) {
    _flagActivityScheduleConflict(sortedList, startingIdx, startingIdx + 1, userId, shouldCheckAccountVisit);
  }
}

export function startRecursiveActivityCloseByActivityFlagging(sortedList: Activity[], startingIdx, userId: string, proximityRange: number, shouldCheckAccountVisit: boolean) {
  _warnCloseByMeeting(sortedList, startingIdx, startingIdx + 1, userId, proximityRange, shouldCheckAccountVisit);
}

/**
 * Optimized activity validation with caching
 */
export function isValidActivityToCheckTimeConflict(activity: Activity, userId: string): boolean {
  if (!activity || !userId) return false;
  
  const cacheKey = getActivityValidationKey(activity, userId);
  
  // Check cache first
  if (activityValidationCache.has(cacheKey)) {
    return activityValidationCache.get(cacheKey)!;
  }
  
  let isValid: boolean = false;
  
  if (activity instanceof AppointmentActivity) {
    isValid = (
      activity.state !== MeetingActivityState.Completed
      && (activity.meetingOwnerId === userId
        || (
          Array.isArray(activity.accompaniedUserList)
          && activity.accompaniedUserList.some(a => a.id === userId)
        ))
    );
  } else if (activity instanceof PhoneActivity) {
    isValid = (
      activity.state !== PhoneCallActivityState.Completed
      && (
        activity.ownerId === userId
        || (
          Array.isArray(activity.accompaniedUserList)
          && activity.accompaniedUserList.some(a => a.id === userId)
        )
      )
    );
  } else if (activity instanceof TimeOffActivity) {
    isValid = activity.totOwnerId === userId;
  } else if (activity instanceof EventActivity) {
    isValid = (
      activity.statuscode === 1
      && (
        activity.ownerId === userId
        || (
          Array.isArray(activity.covisitors)
          && activity.covisitors.some(a => a.id === userId)
        )
      )
    );
  }
  
  // Cache the result
  activityValidationCache.set(cacheKey, isValid);
  clearCacheIfNeeded(activityValidationCache, activityValidationCacheSize);
  
  return isValid;
}

/**
 * Optimized date conflict checking with caching and reduced object creation
 */
function _checkTwoDateRangesConflict(aStart: Date, aEnd: Date, bStart: Date, bEnd: Date): boolean | null {
  if (!aStart || !aEnd || !bStart || !bEnd) return null;
  
  const cacheKey = getDateConflictKey(aStart, aEnd, bStart, bEnd);
  
  // Check cache first
  if (dateConflictCache.has(cacheKey)) {
    return dateConflictCache.get(cacheKey)!;
  }
  
  let isConflict: boolean | null = null;

  try {
    // Optimize: Use getTime() directly instead of creating new Date objects
    const aS = aStart.getTime();
    const aE = aEnd.getTime();
    const bS = bStart.getTime();
    const bE = bEnd.getTime();

    // Remove seconds and milliseconds for consistency
    const aS_normalized = Math.floor(aS / 60000) * 60000;
    const aE_normalized = Math.floor(aE / 60000) * 60000;
    const bS_normalized = Math.floor(bS / 60000) * 60000;
    const bE_normalized = Math.floor(bE / 60000) * 60000;

    isConflict = _checkScheduleOverlap(
      aS_normalized,
      aE_normalized,
      bS_normalized,
      bE_normalized,
    );
    
    // Cache the result
    dateConflictCache.set(cacheKey, isConflict);
    clearCacheIfNeeded(dateConflictCache, dateConflictCacheSize);
    
  } catch (error) {
    console.error('_checkTwoDateRangesConflict: ', error);
  }

  return isConflict;
}

/**
 * Optimized activity end date calculation
 */
function _getRealActivityEndDate(activity: Activity): Date {
  if (activity instanceof TimeOffActivity && activity.durationType === DurationType.AllDay) {
    const end = new Date(activity?.scheduledStart);
    end.setHours(23, 59, 59, 999);
    return end;
  }
  return activity?.scheduledEnd;
}

/**
 * Optimized account visit check with caching
 */
function _isAccountVisitOrNestedMeetingActivity(activity: AppointmentActivity): {
  isAccountVisit: boolean,
  isNestedMeeting: boolean,
} {
  const cacheKey = `${activity.ID}_${activity.indskr_isparentcall}_${activity.indskr_parentcallid}`;
  
  if (accountVisitCache.has(cacheKey)) {
    return accountVisitCache.get(cacheKey)!;
  }
  
  const response = {
    isAccountVisit: false,
    isNestedMeeting: false,
  };

  if (activity.indskr_isparentcall) {
    response.isAccountVisit = true;
  } else if (activity.indskr_parentcallid) {
    response.isNestedMeeting = true;
  }

  accountVisitCache.set(cacheKey, response);
  clearCacheIfNeeded(accountVisitCache, 500);
  
  return response;
}

/**
 * Optimized account visit parent-child check
 */
function _isAccountVisitParentChildOrSibling(a: AppointmentActivity, b: AppointmentActivity): boolean {
  return a.ID === b.indskr_parentcallid || 
         b.ID === a.indskr_parentcallid || 
         a.indskr_parentcallid === b.indskr_parentcallid;
}

/**
 * Optimized conflict flagging with early termination and reduced object creation
 */
function _flagActivityScheduleConflict(sortedList: Activity[], startingIdx: number, nextIdx: number, userId: string, shouldCheckAccountVisit: boolean) {
  try {
    if (nextIdx >= sortedList.length) return;
    
    const baseActivity = sortedList[startingIdx];
    const isBaseActivityValid = isValidActivityToCheckTimeConflict(baseActivity, userId);
    
    // Early termination if base activity is not valid
    if (!isBaseActivityValid) {
      _flagActivityScheduleConflict(sortedList, startingIdx, nextIdx + 1, userId, shouldCheckAccountVisit);
      return;
    }
    
    const isBaseAccountVisitCheck = shouldCheckAccountVisit
      ? _isAccountVisitOrNestedMeetingActivity(baseActivity as AppointmentActivity)
      : { isAccountVisit: false, isNestedMeeting: false };
      
    const activityToCheck = sortedList[nextIdx];
    const isActivityToCheckAccountVisitCheck = shouldCheckAccountVisit
      ? _isAccountVisitOrNestedMeetingActivity(activityToCheck as AppointmentActivity)
      : { isAccountVisit: false, isNestedMeeting: false };

    // Skip if two activities have parent child account visit relationship
    const isAnyNestedMeeting = isBaseAccountVisitCheck.isNestedMeeting || isActivityToCheckAccountVisitCheck.isNestedMeeting;
    const isAccountVisitParentChild = (
      isBaseAccountVisitCheck.isAccountVisit && isActivityToCheckAccountVisitCheck.isNestedMeeting
      || isBaseAccountVisitCheck.isNestedMeeting && isActivityToCheckAccountVisitCheck.isAccountVisit
    )
      ? _isAccountVisitParentChildOrSibling(
          baseActivity as AppointmentActivity,
          activityToCheck as AppointmentActivity
        )
      : false;
      
    if (isAnyNestedMeeting || isAccountVisitParentChild) {
      _flagActivityScheduleConflict(sortedList, startingIdx, nextIdx + 1, userId, shouldCheckAccountVisit);
      return;
    }

    // Optimize: Get end dates once
    const baseActivityEnd = _getRealActivityEndDate(baseActivity);
    const activityToCheckEnd = _getRealActivityEndDate(activityToCheck);

    const isConflict = _checkTwoDateRangesConflict(
      baseActivity?.scheduledStart,
      baseActivityEnd,
      activityToCheck?.scheduledStart,
      activityToCheckEnd,
    );

    if (isConflict === null || isConflict === true) {
      const isActivityToCheckValid = isValidActivityToCheckTimeConflict(activityToCheck, userId);
      if (isConflict && isActivityToCheckValid) {
        if (baseActivity.offlineId && activityToCheck.offlineId) {
          // Initialize maps only if needed
          if (!(baseActivity.conflictingActivityIds instanceof Map)) {
            baseActivity.conflictingActivityIds = new Map();
          }
          if (!(activityToCheck.conflictingActivityIds instanceof Map)) {
            activityToCheck.conflictingActivityIds = new Map();
          }
          
          baseActivity.conflictingActivityIds.set(activityToCheck.offlineId, true);
          activityToCheck.conflictingActivityIds.set(baseActivity.offlineId, true);
        }
      }
      _flagActivityScheduleConflict(sortedList, startingIdx, nextIdx + 1, userId, shouldCheckAccountVisit);
    }
  } catch (error) {
    console.error('_flagActivityScheduleConflict: ', error);
  }
}

/**
 * Optimized schedule overlap check
 */
function _checkScheduleOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean | null {
  if (isNaN(aStart) || isNaN(aEnd) || isNaN(bStart) || isNaN(bEnd)) {
    return null;
  }
  return !(aEnd <= bStart || aStart >= bEnd);
}

/**
 * Optimized event conflict flagging with early termination
 */
export function startRecursiveEventScheduleConflictFlagging(sortedEvents: EventActivity[], activity: Activity, userId: string): number {
  const isActivityValid = isValidActivityToCheckTimeConflict(activity, userId);
  let lastIdx: number = 0;

  if (!isActivityValid || !Array.isArray(sortedEvents) || sortedEvents.length === 0) {
    return lastIdx;
  }

  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTimestamp: number = today.getTime();
    
    const activityStart: Date = activity instanceof EventActivity
      ? activity?._startDate
      : activity?.scheduledStart ?? undefined;
    const activityEnd: Date = activity instanceof EventActivity
      ? activity?.endDate
      : activity?.scheduledEnd ?? undefined;

    // Events are sorted in desc order
    for (let i = 0; i < sortedEvents.length; i++) {
      const eventActivity: EventActivity = sortedEvents[i];
      const eStartTimestamp: number = eventActivity?._startDate?.getTime();
      const eEndTimestamp: number = eventActivity?.endDate?.getTime();

      if (eStartTimestamp >= todayTimestamp || eEndTimestamp >= todayTimestamp) {
        const isConflict: boolean | null = _checkTwoDateRangesConflict(
          eventActivity?._startDate,
          eventActivity?.endDate,
          activityStart,
          activityEnd,
        );

        if (isConflict) {
          if (activity.offlineId && eventActivity.offlineId) {
            if (!(activity.conflictingActivityIds instanceof Map)) {
              activity.conflictingActivityIds = new Map();
            }
            activity.conflictingActivityIds.set(eventActivity.offlineId, true);
            
            if (!(eventActivity.conflictingActivityIds instanceof Map)) {
              eventActivity.conflictingActivityIds = new Map();
            }
            eventActivity.conflictingActivityIds.set(activity.offlineId, true);
          }
          lastIdx = i;
        }
      }
    }
  } catch (error) {
    console.error('startRecursiveEventScheduleConflictFlagging: ', error);
  }

  return lastIdx;
}

/**
 * Optimized event conflict flagging for events array
 */
export function startRecursiveEventScheduleConflictFlaggingForEventsArray(sortedList: EventActivity[], currentIdx: number, lastIdxToBehChecked: number, userId: string): number {
  let lastIdx: number = lastIdxToBehChecked;

  if (!Array.isArray(sortedList) || sortedList.length === 0) {
    return lastIdx;
  }

  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTimestamp: number = today.getTime();

    for (let i = currentIdx; i < sortedList.length; i++) {
      const currentEvent: EventActivity = sortedList[i];
      const eStartTimestamp: number = currentEvent?._startDate?.getTime();
      const eEndTimestamp: number = currentEvent?.endDate?.getTime();

      if (eStartTimestamp >= todayTimestamp || eEndTimestamp >= todayTimestamp) {
        const isConflict: boolean | null = _checkTwoDateRangesConflict(
          currentEvent?._startDate,
          currentEvent?.endDate,
          sortedList[currentIdx]?._startDate,
          sortedList[currentIdx]?.endDate,
        );

        if (isConflict) {
          if (sortedList[currentIdx].offlineId && currentEvent.offlineId) {
            if (!(sortedList[currentIdx].conflictingActivityIds instanceof Map)) {
              sortedList[currentIdx].conflictingActivityIds = new Map();
            }
            sortedList[currentIdx].conflictingActivityIds.set(currentEvent.offlineId, true);
            
            if (!(currentEvent.conflictingActivityIds instanceof Map)) {
              currentEvent.conflictingActivityIds = new Map();
            }
            currentEvent.conflictingActivityIds.set(sortedList[currentIdx].offlineId, true);
          }
          lastIdx = i;
        }
      }
    }
  } catch (error) {
    console.error('startRecursiveEventScheduleConflictFlaggingForEventsArray: ', error);
  }

  return lastIdx;
}

/**
 * Optimized date range overlap check
 */
export function isDateRangeOverlapOrInFuture(startDate: Date, endDate: Date, timestampToCompare: number) {
  if (!startDate || !endDate) return false;
  
  const startTimestamp = startDate.getTime();
  const endTimestamp = endDate.getTime();
  
  return startTimestamp >= timestampToCompare || endTimestamp >= timestampToCompare;
}

/**
 * Optimized new activity conflict check with early termination
 */
export function newActivityConflictCheck(activities: Activity[], activityStartIdx: number, events: EventActivity[], checkStopIdx: number, start: Date, end: Date, userId: string): boolean {
  if (!start || !end || !userId) return false;
  
  // Check events first (usually smaller set)
  if (Array.isArray(events) && events.length > 0) {
    const stopIdx = checkStopIdx > -1 ? checkStopIdx : events.length - 1;
    for (let i = 0; i <= stopIdx; i++) {
      const event = events[i];
      const isConflict: boolean = _checkTwoDateRangesConflict(event?._startDate, event?.endDate, start, end);
      if (isConflict) {
        const isValidActivity: boolean = isValidActivityToCheckTimeConflict(event, userId);
        if (isValidActivity) {
          return true; // Early termination
        }
      }
    }
  }
  
  // Check activities if no event conflict found
  if (Array.isArray(activities) && activities.length > 0) {
    const startIdx = activityStartIdx > -1 ? activityStartIdx : 0;
    for (let i = startIdx; i < activities.length; i++) {
      const activity = activities[i];
      const isConflict: boolean = _checkTwoDateRangesConflict(activity?.scheduledStart, activity?.scheduledEnd, start, end);
      if (isConflict) {
        const isValidActivity: boolean = isValidActivityToCheckTimeConflict(activity, userId);
        if (isValidActivity) {
          return true; // Early termination
        }
      }
    }
  }
  
  return false;
}

/**
 * Optimized proximity warning check with early termination
 */
export function newActivityProximityWarningCheck(activities: Activity[], start: Date, end: Date, userId: string, proximityRange: number, contactId: string): boolean {
  if (proximityRange <= 0 || !activities || activities.length === 0) return false;
  
  // Filter meetings once
  const meetings = activities.filter(a => a.type == ActivityType.Appointment && !isEmpty(a['contacts']));
  if (meetings.length === 0) return false;
  
  // Pre-calculate proximity-adjusted dates
  const newActivityStart: Date = new Date(start);
  newActivityStart.setHours(newActivityStart.getHours() - proximityRange);
  const newActivityEnd: Date = new Date(end);
  newActivityEnd.setHours(newActivityEnd.getHours() + proximityRange);
  
  for (let i = 0; i < meetings.length; i++) {
    const activity = meetings[i];
    const isValidActivity = isValidMeetingToCheckCloseByMeeting(activity, userId);
    if (isValidActivity && activity['contacts'].some(c => c.ID == contactId)) {
      const hasProximityWarning = _checkTwoDateRangesConflict(
        activity?.scheduledStart, 
        activity?.scheduledEnd, 
        newActivityStart, 
        newActivityEnd
      );
      if (hasProximityWarning) { 
        return true; // Early termination
      }
    }
  }
  
  return false;
}

/**
 * Optimized conflict check against activities
 */
export function checkConflictAgainstActivities(sortedList: Activity[], activity: Activity, checkStartIdx: number, userId: string) {
  try {
    if (!Array.isArray(sortedList) || sortedList.length === 0 || !userId || !activity) {
      return;
    }
    
    if (checkStartIdx < 0 || checkStartIdx >= sortedList.length) {
      checkStartIdx = 0;
    }
    
    const startDate: Date = activity instanceof EventActivity ? activity._startDate : activity.scheduledStart;
    const endDate: Date = activity instanceof EventActivity ? activity.endDate : activity.scheduledEnd;

    if (activity instanceof TimeOffActivity && startDate?.getTime() === endDate?.getTime()) {
      // 1 day time off record has same start & end date time
      endDate?.setHours(23, 59, 59, 999);
    }

    for (let i = checkStartIdx; i < sortedList.length; i++) {
      const activityToCheck = sortedList[i];

      if (activityToCheck.offlineId !== activity.offlineId && isValidActivityToCheckTimeConflict(activityToCheck, userId)) {
        const isConflict = _checkTwoDateRangesConflict(
          activityToCheck.scheduledStart,
          _getRealActivityEndDate(activityToCheck),
          startDate,
          endDate,
        );

        if (isConflict) {
          if (!(activityToCheck.conflictingActivityIds instanceof Map)) {
            activityToCheck.conflictingActivityIds = new Map();
          }
          activityToCheck.conflictingActivityIds.set(activity.offlineId, true);

          if (!(activity.conflictingActivityIds instanceof Map)) {
            activity.conflictingActivityIds = new Map();
          }
          activity.conflictingActivityIds.set(activityToCheck.offlineId, true);
        }
      }
    }
  } catch (error) {
    console.error('checkConflictAgainstActivities: ', error);
  }
}

/**
 * Optimized conflict check against events
 */
export function checkConflictAgainstEvents(sortedList: EventActivity[], activity: Activity, lastIdxToBehChecked: number, userId: string) {
  try {
    if (!Array.isArray(sortedList) || sortedList.length === 0 || !userId || !activity) {
      return;
    }
    
    const startDate: Date = activity instanceof EventActivity ? activity._startDate : activity.scheduledStart;
    const endDate: Date = activity instanceof EventActivity ? activity.endDate : activity.scheduledEnd;

    if (activity instanceof TimeOffActivity && startDate?.getTime() === endDate?.getTime()) {
      endDate?.setHours(23, 59, 59, 999);
    }

    const stopIdx = lastIdxToBehChecked > -1 ? lastIdxToBehChecked : sortedList.length - 1;
    
    for (let i = 0; i <= stopIdx; i++) {
      const eventToCheck = sortedList[i];

      if (eventToCheck.offlineId !== activity.offlineId && isValidActivityToCheckTimeConflict(eventToCheck, userId)) {
        const isConflict = _checkTwoDateRangesConflict(
          eventToCheck._startDate,
          eventToCheck.endDate,
          startDate,
          endDate,
        );

        if (isConflict) {
          if (!(eventToCheck.conflictingActivityIds instanceof Map)) {
            eventToCheck.conflictingActivityIds = new Map();
          }
          eventToCheck.conflictingActivityIds.set(activity.offlineId, true);

          if (!(activity.conflictingActivityIds instanceof Map)) {
            activity.conflictingActivityIds = new Map();
          }
          activity.conflictingActivityIds.set(eventToCheck.offlineId, true);
        }
      }
    }
  } catch (error) {
    console.error('checkConflictAgainstEvents: ', error);
  }
}

/**
 * Optimized conflict removal
 */
export function removeConflictIdFromOtherActivities(activity: Activity, activities: Activity[], events: EventActivity[]) {
  try {
    if (!activity?.offlineId) return;
    
    // Remove from activities
    if (Array.isArray(activities)) {
      for (const act of activities) {
        if (act.conflictingActivityIds instanceof Map) {
          act.conflictingActivityIds.delete(activity.offlineId);
        }
      }
    }
    
    // Remove from events
    if (Array.isArray(events)) {
      for (const event of events) {
        if (event.conflictingActivityIds instanceof Map) {
          event.conflictingActivityIds.delete(activity.offlineId);
        }
      }
    }
  } catch (error) {
    console.error('removeConflictIdFromOtherActivities: ', error);
  }
}

/**
 * Optimized time conflict check with other activities
 */
export function checkTimeConflictWithOtherActivities(
  activity: Activity,
  activities: Activity[],
  activityConflictCheckStartIdx: number,
  eventsToolData: EventActivity[],
  eventConflictCheckLastIdx: number,
  userId: string,
) {
  try {
    if (!activity || !userId) return;
    
    checkConflictAgainstActivities(activities, activity, activityConflictCheckStartIdx, userId);
    checkConflictAgainstEvents(eventsToolData, activity, eventConflictCheckLastIdx, userId);
  } catch (error) {
    console.error('checkTimeConflictWithOtherActivities: ', error);
  }
}

/**
 * Optimized proximity warning with early termination and reduced object creation
 */
function _warnCloseByMeeting(sortedList: Activity[], startIdx: number, nextIdx: number, userId: string, proximityRange: number, shouldCheckAccountVisit: boolean) {
  try {
    if (nextIdx >= sortedList.length) return;
    
    const baseActivity: AppointmentActivity = sortedList[startIdx] as AppointmentActivity;
    const isBaseAccountVisitCheck = shouldCheckAccountVisit 
      ? _isAccountVisitOrNestedMeetingActivity(baseActivity)
      : { isAccountVisit: false, isNestedMeeting: false };
      
    // Pre-calculate proximity-adjusted dates for base activity
    const baseActivityStart: Date = new Date(baseActivity?.scheduledStart);
    baseActivityStart.setHours(baseActivityStart.getHours() - proximityRange);
    const baseActivityEnd: Date = new Date(baseActivity?.scheduledEnd);
    baseActivityEnd.setHours(baseActivityEnd.getHours() + proximityRange);
    
    for (let j = nextIdx; j < sortedList.length; j++) {
      const activityToCheck: AppointmentActivity = sortedList[j] as AppointmentActivity;
      const isActivityToCheckAccountVisitCheck = shouldCheckAccountVisit 
        ? _isAccountVisitOrNestedMeetingActivity(activityToCheck)
        : { isAccountVisit: false, isNestedMeeting: false };
        
      // Skip if two activities have parent child account visit relationship
      const isAnyNestedMeeting = isBaseAccountVisitCheck.isNestedMeeting || isActivityToCheckAccountVisitCheck.isNestedMeeting;
      const isAccountVisitParentChild = (
        isBaseAccountVisitCheck.isAccountVisit && isActivityToCheckAccountVisitCheck.isNestedMeeting
        || isBaseAccountVisitCheck.isNestedMeeting && isActivityToCheckAccountVisitCheck.isAccountVisit
      ) ? _isAccountVisitParentChildOrSibling(baseActivity, activityToCheck) : false;
      
      if (isAnyNestedMeeting || isAccountVisitParentChild) {
        _warnCloseByMeeting(sortedList, startIdx, j + 1, userId, proximityRange, shouldCheckAccountVisit);
        return;
      }
      
      // Check if appointments have the same contact
      if (intersectionBy(baseActivity['contacts'], activityToCheck['contacts'], 'ID').length > 0) {
        const activityToCheckStart: Date = activityToCheck?.scheduledStart;
        const activityToCheckEnd: Date = activityToCheck?.scheduledEnd;
        const isConflict = _checkTwoDateRangesConflict(baseActivityStart, baseActivityEnd, activityToCheckStart, activityToCheckEnd);
        
        if (isConflict === null || isConflict === true) {
          if (isConflict) {
            if (baseActivity.offlineId && activityToCheck.offlineId) {
              // Initialize maps only if needed
              if (!(baseActivity.closeByActivityIds instanceof Map)) {
                baseActivity.closeByActivityIds = new Map();
              }
              if (!(activityToCheck.closeByActivityIds instanceof Map)) {
                activityToCheck.closeByActivityIds = new Map();
              }
              
              baseActivity.closeByActivityIds.set(activityToCheck.offlineId, true);
              activityToCheck.closeByActivityIds.set(baseActivity.offlineId, true);
              
              // Handle completed activity IDs
              if (!(baseActivity.closeByCompletedActivityIds instanceof Map)) {
                baseActivity.closeByCompletedActivityIds = new Map();
              }
              if (!(activityToCheck.closeByCompletedActivityIds instanceof Map)) {
                activityToCheck.closeByCompletedActivityIds = new Map();
              }
              
              if (activityToCheck.isCompleted) {
                baseActivity.closeByCompletedActivityIds.set(activityToCheck.offlineId, true);
              }
              if (baseActivity.isCompleted) {
                activityToCheck.closeByCompletedActivityIds.set(baseActivity.offlineId, true);
              }
            }
          }
        }
      }
    }
  } catch (error) {
    console.error('_warnCloseByMeeting: ', error);
  }
}

/**
 * Optimized proximity warning check against activities
 */
export function checkProximityWarningAgainstActivities(activity: Activity, sortedList: Activity[], userId: string, proximityRange: number) {
  try {
    if (!activity || proximityRange <= 0 || !sortedList || sortedList.length === 0) return;
    
    activity.closeByActivityIds = new Map();
    
    // Filter meetings once
    const meetings = sortedList.filter(a => 
      a.type == ActivityType.Appointment && 
      a.offlineId !== activity.offlineId && 
      !isEmpty(a['contacts'])
    );
    
    if (meetings.length === 0) return;
    
    // Pre-calculate proximity-adjusted dates
    const startDate: Date = new Date(activity?.scheduledStart);
    startDate.setHours(startDate.getHours() - proximityRange);
    const endDate: Date = new Date(activity?.scheduledEnd);
    endDate.setHours(endDate.getHours() + proximityRange);
    
    for (let i = 0; i < meetings.length; i++) {
      const activityToCheck = meetings[i];
      if (isValidMeetingToCheckCloseByMeeting(activityToCheck, userId) && 
          intersectionBy(activity['contacts'], activityToCheck['contacts'], 'ID').length > 0) {
        
        const isConflict = _checkTwoDateRangesConflict(
          activityToCheck.scheduledStart, 
          activityToCheck?.scheduledEnd, 
          startDate, 
          endDate
        );
        
        if (isConflict) {
          if (!(activityToCheck.closeByActivityIds instanceof Map)) {
            activityToCheck.closeByActivityIds = new Map();
          }
          activityToCheck.closeByActivityIds.set(activity.offlineId, true);

          if (!(activity.closeByActivityIds instanceof Map)) {
            activity.closeByActivityIds = new Map();
          }
          activity.closeByActivityIds.set(activityToCheck.offlineId, true);
        }
      }
    }
  } catch (error) {
    console.error('checkProximityWarningAgainstActivities: ', error);
  }
}

/**
 * Optimized meeting validation for proximity checks
 */
function isValidMeetingToCheckCloseByMeeting(activity: Activity, userId: string): boolean {
  return activity instanceof AppointmentActivity && 
         (activity.meetingOwnerId === userId ||
          (Array.isArray(activity.accompaniedUserList) && 
           activity.accompaniedUserList.some(a => a.id === userId))) && 
         !isEmpty(activity.contacts);
}
